import React, { useEffect, useState, useCallback } from 'react';
import { usePersistedState } from '@/hooks/usePersistedState';
import axios from 'axios';
import { ResponsiveLayout } from '../components/ResponsiveLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DiagnosisReportModal from '../components/DiagnosisReportModal';
import {
  Stethoscope, Search, History, Trash2, Loader2, Eye, Sparkles,
  Grid3x3, ListChecks, User as UserIcon, Clock, Filter, ChevronLeft, ChevronRight, ArrowLeftRight,
} from 'lucide-react';
import { toast } from 'sonner';
import DiagnosisCompareTab from '../components/DiagnosisCompareTab';

const API = process.env.REACT_APP_BACKEND_URL;

const toPeriod = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const shiftPeriod = (p, delta) => {
  const [y, m] = p.split('-').map(Number);
  return toPeriod(new Date(y, m - 1 + delta, 1));
};
const fmtPeriod = (p) => {
  const [y, m] = p.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });
};
// Last 24 months, newest first (includes current month)
const monthOptions = Array.from({ length: 24 }, (_, i) => shiftPeriod(toPeriod(new Date()), -i));

function ChannelAvatar({ name, logoUrl, size = 40 }) {
  const initials = (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  // Colour hash from name for consistent per-channel colour
  const hue = Array.from(name || '').reduce((a, c) => (a + c.charCodeAt(0)) % 360, 0);
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        style={{ width: size, height: size }}
        className="rounded-lg object-cover border border-[hsl(var(--border))]"
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, hsl(${hue},70%,25%) 0%, hsl(${(hue + 40) % 360},70%,15%) 100%)`,
      }}
      className="rounded-lg flex items-center justify-center text-white font-bold border border-[hsl(var(--border))]"
    >
      <span style={{ fontSize: size / 2.6 }}>{initials}</span>
    </div>
  );
}

function ChannelsTab({ ibs, historyByIb, openHistoryFor, toggleHistory, handleDiagnose, handleViewHistoryReport, handleDeleteReport, defaultMode, setDefaultMode }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {ibs.map((ib) => {
        const historyRows = historyByIb[ib.id] || [];
        const isOpen = openHistoryFor === ib.id;
        return (
          <div key={ib.id} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-4 hover:border-[#E63946]/40 transition-all" data-testid={`diagnosis-card-${ib.id}`}>
            <div className="flex items-start gap-3 mb-4">
              <ChannelAvatar name={ib.name} logoUrl={ib.brand_logo_url} size={44} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate">{ib.name}</div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#10b981]/15 text-[#10b981]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" /> Active
                  </span>
                  {typeof ib.health_score === 'number' && (
                    <span className="text-[9px] text-[hsl(var(--text-muted))]">Health {Math.round(ib.health_score)}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 h-9 text-[11px] bg-gradient-to-r from-[#E63946] to-[#c62828] hover:brightness-110"
                onClick={() => handleDiagnose(ib, defaultMode)}
                data-testid={`diagnose-btn-${ib.id}`}
              >
                <Sparkles size={12} className="mr-1" />
                Diagnose
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 px-3"
                onClick={() => toggleHistory(ib.id)}
                data-testid={`history-btn-${ib.id}`}
                title="View past reports"
              >
                <History size={12} />
                {historyRows.length > 0 && (
                  <span className="ml-1 text-[10px] font-bold text-[#E63946]">{historyRows.length}</span>
                )}
              </Button>
            </div>
            {isOpen && (
              <div className="mt-3 border-t border-[hsl(var(--border))] pt-3 space-y-1.5" data-testid={`history-list-${ib.id}`}>
                <div className="text-[9px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider mb-1">History</div>
                {historyRows.length === 0 ? (
                  <div className="text-[10px] text-[hsl(var(--text-muted))] italic">Belum ada report</div>
                ) : (
                  historyRows.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 py-1 px-2 rounded hover:bg-[hsl(var(--surface-elevated))]">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-white">
                          {new Date(r.generated_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · Day {r.day_snapshot}
                        </div>
                        <div className="text-[9px] text-[hsl(var(--text-muted))] truncate italic">
                          {r.ai_report?.kesimpulan || '—'}
                        </div>
                      </div>
                      <button
                        className="p-1 rounded hover:bg-[hsl(var(--surface-overlay))] text-[hsl(var(--text-muted))] hover:text-white"
                        onClick={() => handleViewHistoryReport(ib, r.id)}
                        title="View"
                        data-testid={`view-history-${r.id}`}
                      >
                        <Eye size={11} />
                      </button>
                      <button
                        className="p-1 rounded hover:bg-[#ef4444]/10 text-[hsl(var(--text-muted))] hover:text-[#ef4444]"
                        onClick={() => handleDeleteReport(r.id, ib.id)}
                        title="Delete"
                        data-testid={`delete-history-${r.id}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GlobalLogTab({ handleViewReport }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/diagnosis/history`, { headers: { Authorization: `Bearer ${token}` } });
        setRows(res.data.reports || []);
      } catch (e) {
        toast.error('Failed to load history');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-12 text-center"><Loader2 className="animate-spin text-[#E63946] mx-auto" size={24} /></div>;

  if (rows.length === 0) {
    return (
      <div className="p-12 text-center">
        <ListChecks size={40} className="text-[hsl(var(--text-muted))] mx-auto mb-3" />
        <div className="text-sm text-white font-bold mb-1">Belum ada diagnosis</div>
        <div className="text-xs text-[hsl(var(--text-muted))]">Klik Diagnose pada mana-mana channel untuk mula.</div>
      </div>
    );
  }

  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider">
          Diagnosis Audit Log — {rows.length} report{rows.length !== 1 && 's'}
        </div>
      </div>
      <table className="w-full text-[11px]" data-testid="diagnosis-audit-table">
        <thead>
          <tr className="bg-[hsl(var(--surface-elevated))] text-[9px] uppercase text-[hsl(var(--text-muted))]">
            <th className="py-2 px-3 text-left">When</th>
            <th className="py-2 px-3 text-left">Channel</th>
            <th className="py-2 px-3 text-left">Period</th>
            <th className="py-2 px-3 text-left">Generated By</th>
            <th className="py-2 px-3 text-left max-w-[300px]">Kesimpulan</th>
            <th className="py-2 px-3 text-right w-20">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))]" data-testid={`audit-row-${r.id}`}>
              <td className="py-2.5 px-3 text-white">
                {new Date(r.generated_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </td>
              <td className="py-2.5 px-3 font-semibold text-white">{r.ib_name}</td>
              <td className="py-2.5 px-3 text-[hsl(var(--text-muted))] font-mono">{r.period_current} · Day {r.day_snapshot}</td>
              <td className="py-2.5 px-3 text-[hsl(var(--text-muted))]">
                <span className="inline-flex items-center gap-1"><UserIcon size={10} /> {r.generated_by_name || '—'}</span>
              </td>
              <td className="py-2.5 px-3 italic text-[10px] text-[hsl(var(--text-muted))] max-w-[300px]">
                <div className="line-clamp-2">{r.ai_report?.kesimpulan || '—'}</div>
              </td>
              <td className="py-2.5 px-3 text-right">
                <button
                  className="p-1.5 rounded hover:bg-[hsl(var(--surface-overlay))] text-[hsl(var(--text-muted))] hover:text-white"
                  onClick={() => handleViewReport(r)}
                  title="View report"
                  data-testid={`audit-view-${r.id}`}
                >
                  <Eye size={12} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DiagnosisCenterPage() {
  const [ibs, setIbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistedState('diagnosis:search', '');
  const [tab, setTab] = usePersistedState('diagnosis:tab', 'channels'); // 'channels' | 'log'
  const [activeIb, setActiveIb] = useState(null);
  const [existingReport, setExistingReport] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [historyByIb, setHistoryByIb] = useState({});
  const [openHistoryFor, setOpenHistoryFor] = useState(null);
  const [diagMode, setDiagMode] = usePersistedState('diagnosis:mode', 'mtd'); // 'mtd' | 'last_7_days' | 'last_30_days' | 'month'
  const [diagPeriod, setDiagPeriod] = useState(() => shiftPeriod(toPeriod(new Date()), -1)); // default bulan lepas (dah lengkap)

  const fetchIbs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/ib/list`, { headers: { Authorization: `Bearer ${token}` } });
      const all = res.data.ibs || res.data || [];
      // Show only active channels — inactive channels are clutter in the
      // diagnosis workflow.
      const active = all.filter((ib) => ((ib.status || 'active').toLowerCase()) === 'active');
      setIbs(active);
    } catch (e) {
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (ibId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/diagnosis/history/${ibId}`, { headers: { Authorization: `Bearer ${token}` } });
      setHistoryByIb((prev) => ({ ...prev, [ibId]: res.data.reports || [] }));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchIbs(); }, [fetchIbs]);

  const filtered = ibs.filter((ib) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (ib.name || '').toLowerCase().includes(q);
  });

  const handleDiagnose = (ib, mode = 'mtd') => {
    setActiveIb({ ib_id: ib.id, ib_name: ib.name, brand_logo_url: ib.brand_logo_url, mode, period: diagPeriod });
    setExistingReport(null);
    setModalOpen(true);
  };

  const handleViewHistoryReport = async (ib, reportId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/diagnosis/${reportId}`, { headers: { Authorization: `Bearer ${token}` } });
      setActiveIb({ ib_id: ib.id, ib_name: ib.name, brand_logo_url: ib.brand_logo_url });
      setExistingReport(res.data);
      setModalOpen(true);
    } catch (e) {
      toast.error('Failed to load report');
    }
  };

  const handleViewAuditReport = async (r) => {
    // Look up the channel to grab logo if available
    const ib = ibs.find((x) => x.id === r.ib_id) || { id: r.ib_id, name: r.ib_name };
    await handleViewHistoryReport(ib, r.id);
  };

  const handleDeleteReport = async (reportId, ibId) => {
    if (!window.confirm('Delete this diagnosis report?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/diagnosis/${reportId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Report deleted');
      fetchHistory(ibId);
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const toggleHistory = (ibId) => {
    if (openHistoryFor === ibId) {
      setOpenHistoryFor(null);
    } else {
      setOpenHistoryFor(ibId);
      if (!historyByIb[ibId]) fetchHistory(ibId);
    }
  };

  return (
    <ResponsiveLayout>
      <div className="flex flex-col min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))]">
        <header className="sticky top-0 z-30 bg-[hsl(var(--surface))]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
          <div className="px-4 lg:px-6 py-3 lg:py-4 flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E63946] to-[#7c3aed] flex items-center justify-center shadow-lg shadow-[#E63946]/25">
              <Stethoscope size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg lg:text-xl font-bold tracking-tight text-white">Channel Diagnostician</h1>
              <p className="text-xs text-[hsl(var(--text-muted))]">
                Satu klik — full AI diagnosis MTD vs bulan lepas. Bahasa mudah, actionable.
              </p>
            </div>
            {/* Tabs */}
            <div className="inline-flex bg-[hsl(var(--surface-elevated))] rounded-lg p-1 border border-[hsl(var(--border))]" data-testid="diagnosis-tabs">
              <button
                onClick={() => setTab('channels')}
                className={`px-3 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1.5 transition-all ${tab === 'channels' ? 'bg-[#E63946] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
                data-testid="tab-channels"
              >
                <Grid3x3 size={12} /> Channels
              </button>
              <button
                onClick={() => setTab('compare')}
                className={`px-3 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1.5 transition-all ${tab === 'compare' ? 'bg-[#E63946] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
                data-testid="tab-compare"
              >
                <ArrowLeftRight size={12} /> Compare
              </button>
              <button
                onClick={() => setTab('log')}
                className={`px-3 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1.5 transition-all ${tab === 'log' ? 'bg-[#E63946] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
                data-testid="tab-log"
              >
                <Clock size={12} /> Audit Log
              </button>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4">
          {tab === 'channels' && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
                  <Input
                    placeholder="Cari channel..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"
                    data-testid="diagnosis-search"
                  />
                </div>
                <div className="inline-flex bg-[hsl(var(--surface-elevated))] rounded-lg p-1 border border-[hsl(var(--border))]" data-testid="diagnosis-mode-selector">
                  {[
                    { key: 'mtd', label: 'MTD Bulan Ini' },
                    { key: 'last_7_days', label: 'Last 7 Days' },
                    { key: 'last_30_days', label: 'Last 30 Days' },
                    { key: 'month', label: 'Bulan Penuh' },
                  ].map((m) => (
                    <button
                      key={m.key}
                      onClick={() => setDiagMode(m.key)}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${diagMode === m.key ? 'bg-[#E63946] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
                      data-testid={`mode-${m.key}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                {diagMode === 'month' && (
                  <div className="inline-flex items-center gap-1.5 bg-[hsl(var(--surface-elevated))] rounded-lg px-2 py-1 border border-[hsl(var(--border))]">
                    <button
                      onClick={() => setDiagPeriod(shiftPeriod(diagPeriod, -1))}
                      className="p-1 rounded hover:bg-[hsl(var(--surface-overlay))] text-[hsl(var(--text-muted))] hover:text-white"
                      data-testid="month-prev"
                      title="Bulan sebelum"
                    >
                      <ChevronLeft size={13} />
                    </button>
                    <select
                      value={diagPeriod}
                      onChange={(e) => setDiagPeriod(e.target.value)}
                      className="h-6 bg-transparent text-[11px] font-semibold text-white outline-none cursor-pointer"
                      data-testid="month-select"
                    >
                      {monthOptions.map((p) => (
                        <option key={p} value={p} className="bg-[#0a0a0f]">{fmtPeriod(p)}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setDiagPeriod(shiftPeriod(diagPeriod, 1))}
                      className="p-1 rounded hover:bg-[hsl(var(--surface-overlay))] text-[hsl(var(--text-muted))] hover:text-white"
                      data-testid="month-next"
                      title="Bulan seterusnya"
                    >
                      <ChevronRight size={13} />
                    </button>
                  </div>
                )}
                <span className="text-[10px] text-[hsl(var(--text-muted))] inline-flex items-center gap-1">
                  <Filter size={11} /> Active only · {filtered.length} channel{filtered.length !== 1 && 's'}
                </span>
              </div>

              {loading ? (
                <div className="p-12 text-center"><Loader2 className="animate-spin text-[#E63946] mx-auto" size={24} /></div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-xs text-[hsl(var(--text-muted))]">Tiada channel active padan.</div>
              ) : (
                <ChannelsTab
                  ibs={filtered}
                  historyByIb={historyByIb}
                  openHistoryFor={openHistoryFor}
                  toggleHistory={toggleHistory}
                  handleDiagnose={handleDiagnose}
                  handleViewHistoryReport={handleViewHistoryReport}
                  handleDeleteReport={handleDeleteReport}
                  defaultMode={diagMode}
                  setDefaultMode={setDiagMode}
                />
              )}
            </>
          )}
          {tab === 'compare' && (
            <DiagnosisCompareTab
              ibs={ibs}
              monthOptions={monthOptions}
              fmtPeriod={fmtPeriod}
              shiftPeriod={shiftPeriod}
              toPeriod={toPeriod}
            />
          )}
          {tab === 'log' && <GlobalLogTab handleViewReport={handleViewAuditReport} />}
        </div>

        {activeIb && (
          <DiagnosisReportModal
            open={modalOpen}
            onOpenChange={(v) => {
              setModalOpen(v);
              if (!v) {
                const justDiagnosed = activeIb?.ib_id;
                setActiveIb(null);
                setExistingReport(null);
                if (justDiagnosed) fetchHistory(justDiagnosed);
              }
            }}
            ibId={activeIb.ib_id}
            ibName={activeIb.ib_name}
            brandLogoUrl={activeIb.brand_logo_url}
            existingReport={existingReport}
            mode={activeIb.mode || 'mtd'}
            period={activeIb.period}
          />
        )}
      </div>
    </ResponsiveLayout>
  );
}
